import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@mui/material";
import { getProfile } from "../redux/asyncThunk/userActions";
import formatDateToDDMMYYYY, { formatDateTimeRu } from "../utils/formatDate";
import AppPageShell from "./AppPageShell";
import { userSlice } from "../redux/user/userSlice";
import ProfileChangePassword from "./profile/ProfileChangePassword";
import TeamAdminsPanel from "./TeamAdminsPanel";

import {
  formatPaymentPrice,
  formatSubscriptionExpiry,
  formatBusinessPlanLabel,
  getTrialStatusText,
  getPaymentPackageLabel,
  getUserRoleLabel,
} from "../utils/userProfile";

const { showDialog } = userSlice;

function ProfileInfoRow({ label, value, valueClassName }) {
  return (
    <div className="app-profile-info-list__row">
      <span>{label}</span>
      <strong className={valueClassName}>{value}</strong>
    </div>
  );
}

export default function Profile() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.data);
  const profile = useSelector((state) => state.user?.profile);
  const games = useSelector((state) => state.user?.games);

  useEffect(() => {
    if (user?.user.id) {
      dispatch(getProfile({ id: user.user.id }));
    }
  }, [dispatch, user?.user.id]);

  const email = profile?.login ?? user?.user?.login ?? "—";
  const role = getUserRoleLabel(profile?.type ?? user?.user?.type);
  const scenariosCount = games?.length ?? 0;
  const emailConfirmed = profile?.email?.confirm ? "Да" : "Нет";
  const telegramLinked = profile?.tgId?.confirm
    ? "Привязан"
    : profile?.tgId?.confirmCode
      ? "Ожидает"
      : "Нет";
  const registeredAt = profile?.createdAt
    ? formatDateToDDMMYYYY(profile.createdAt)
    : "—";
  const payments = profile?.payments ?? [];
  const businessSubscription = profile?.businessSubscription ?? null;
  const businessUntil = formatSubscriptionExpiry(businessSubscription);
  const businessPlan = formatBusinessPlanLabel(businessSubscription);
  const trialStatus = getTrialStatusText(businessSubscription);

  return (
    <AppPageShell title="Профиль" className="app-page--profile">
      <div className="app-profile-layout">
        <div className="app-profile-top-row">
          <section className="app-profile-card app-profile-card--user">
            <h3 className="app-profile-card__title">О пользователе</h3>

            <div className="app-profile-info-list">
              <ProfileInfoRow label="Электронная почта" value={email} />
              <ProfileInfoRow label="ID аккаунта" value={user?.user?.id ?? "—"} />
              <ProfileInfoRow label="Роль" value={role} />
              <ProfileInfoRow label="Дата регистрации" value={registeredAt} />
              <ProfileInfoRow
                label="Почта подтверждена"
                value={emailConfirmed}
                valueClassName={
                  profile?.email?.confirm
                    ? "app-profile-info-row__value--ok"
                    : "app-profile-info-row__value--muted"
                }
              />
              <ProfileInfoRow label="Telegram" value={telegramLinked} />
              <ProfileInfoRow
                label="Сценариев"
                value={String(scenariosCount)}
              />
              <ProfileInfoRow
                label="Подписка"
                value={
                  businessUntil
                    ? trialStatus ?? `${businessPlan ?? "Активна"} до ${businessUntil}`
                    : "Не подключена"
                }
              />
              <ProfileInfoRow
                label="Оплаченных заказов"
                value={String(payments.length)}
              />
              <ProfileInfoRow
                label="Контент 18+"
                value={
                  profile?.accessAdultContent ? "Подтверждён" : "Не подтверждён"
                }
              />
            </div>
          </section>

          <section className="app-profile-card app-profile-card--security">
            <h3 className="app-profile-card__title">Безопасность</h3>
            <ProfileChangePassword userId={user?.user?.id} />
          </section>
        </div>

        <div className="app-profile-sections">
          <TeamAdminsPanel />
          <section className="app-profile-card app-profile-card--compact app-profile-card--payments">
            <div className="app-profile-card__title-row">
              <h3 className="app-profile-card__title">История оплат</h3>
              {payments.length === 0 ? (
                <Link to="/main/price" className="app-profile-inline-link">
                  Тарифы
                </Link>
              ) : null}
            </div>

            {payments.length === 0 ? (
              <p className="app-profile-muted app-profile-muted--compact">
                Пока нет оплаченных заказов
              </p>
            ) : (
              <ul className="app-profile-payments-list">
                {payments.map((payment) => (
                  <li key={payment.id} className="app-profile-payments-list__item">
                    <div className="app-profile-payments-list__main">
                      <span className="app-profile-payments-list__amount">
                        {formatPaymentPrice(payment.price)}
                      </span>
                      <span className="app-profile-payments-list__package">
                        {getPaymentPackageLabel(
                          payment.packageSize,
                          payment.packageId,
                        )}
                      </span>
                    </div>
                    <div className="app-profile-payments-list__meta">
                      <span>
                        {payment.created_at
                          ? formatDateTimeRu(payment.created_at)
                          : "—"}
                      </span>
                      {payment.receiptUrl ? (
                        <a
                          href={payment.receiptUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="app-profile-inline-link"
                        >
                          Квитанция
                        </a>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="app-profile-footer-bar">
          <Button
            color="error"
            size="small"
            variant="outlined"
            className="app-profile-delete-btn"
            onClick={() => dispatch(showDialog("delete-accaunt"))}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Удалить аккаунт
          </Button>
        </div>
      </div>
    </AppPageShell>
  );
}
